import React, { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
  Loader2, PlusCircle, FileText, Play, Trash2, Pencil, ShieldCheck, Check, X,
  Tag, AlertTriangle, Clock, Ban, ExternalLink, Car, KeyRound, Users, Fuel, Truck,
  LayoutDashboard, CalendarClock, MessageSquare, Megaphone, Wallet, Percent, Search, Send, MailCheck, BadgeCheck, BadgePercent,
  MapPin, Eye, Star, ArrowLeft,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fileUrl } from "@/lib/api";
import TripChat from "@/components/TripChat";
import { AdminVerifiedBadge } from "@/components/AdminVerifiedBadge";

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { key: "users", label: "Users", icon: Users },
  { key: "providers", label: "All Providers", icon: KeyRound },
  { key: "hosts", label: "Approvals", icon: ShieldCheck },
  { key: "agents", label: "Agent Management", icon: BadgePercent },
  { key: "tours", label: "Tour Management", icon: MapPin },
  { key: "bookings", label: "Bookings", icon: CalendarClock },
  { key: "messages", label: "Messages", icon: MessageSquare },
  { key: "broadcast", label: "Broadcast", icon: Megaphone },
  { key: "create", label: "Post Content", icon: PlusCircle },
  { key: "content", label: "Content Library", icon: FileText },
  { key: "moderation", label: "Moderation", icon: ShieldCheck },
  { key: "keywords", label: "Flagged Keywords", icon: Tag },
  { key: "car-providers", label: "Car Providers", icon: KeyRound },
  { key: "vehicles", label: "Vehicles", icon: Car },
];

const EMPTY = { type: "article", title: "", excerpt: "", body: "", cover_image: "", video_url: "", tags: "", published: true };

export default function AdminPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "dashboard");
  const [profileUid, setProfileUid] = useState(searchParams.get("user") || null);
  const [focusBooking, setFocusBooking] = useState(searchParams.get("booking") || null);
  const [summary, setSummary] = useState({ pending_threads: 0, blocked_threads: 0, blocked_comments: 0 });
  const [carSummary, setCarSummary] = useState({ pending_cars: 0, pending_providers: 0 });
  const [hostPending, setHostPending] = useState(0);
  const [providerPending, setProviderPending] = useState(0);
  const [tourPending, setTourPending] = useState(0);

  const loadSummary = useCallback(() => {
    api.get("/admin/moderation/summary").then(({ data }) => setSummary(data)).catch(() => {});
    api.get("/admin/cars/summary").then(({ data }) => setCarSummary(data)).catch(() => {});
    api.get("/admin/host-approvals", { params: { status: "pending" } }).then(({ data }) => setHostPending(data.length)).catch(() => {});
    api.get("/admin/providers", { params: { status: "pending" } }).then(({ data }) => setProviderPending(data.length)).catch(() => {});
    api.get("/admin/tours/summary").then(({ data }) => setTourPending(data.pending)).catch(() => {});
  }, []);
  useEffect(() => { loadSummary(); }, [loadSummary]);

  // Deep-link from admin notifications: /admin?tab=hosts&user=<id> or ?tab=bookings&booking=<id>
  useEffect(() => {
    const t = searchParams.get("tab");
    if (t) setTab(t);
    setProfileUid(searchParams.get("user") || null);
    setFocusBooking(searchParams.get("booking") || null);
  }, [searchParams]);

  const changeTab = (key) => {
    setTab(key);
    loadSummary();
    const p = new URLSearchParams(searchParams);
    p.set("tab", key); p.delete("user"); p.delete("booking");
    setSearchParams(p, { replace: true });
  };

  const closeProfile = () => {
    setProfileUid(null);
    const p = new URLSearchParams(searchParams);
    p.delete("user");
    setSearchParams(p, { replace: true });
    loadSummary();
  };

  const modCount = summary.pending_threads + summary.blocked_threads + summary.blocked_comments;

  return (
    <div className="min-h-screen bg-[#060810]">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-8">
          <p className="text-xs font-mono-caption uppercase tracking-[0.3em] text-cyan-400 mb-2">Admin</p>
          <h1 className="font-heading font-extrabold text-white text-4xl">Content & Community</h1>
        </div>

        <div className="flex flex-wrap gap-2 mb-8 border-b border-[#1D2640] pb-4">
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                data-testid={`admin-tab-${t.key}`}
                onClick={() => changeTab(t.key)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold transition-colors ${
                  tab === t.key ? "btn-neon" : "glass text-slate-300 hover:text-cyan-400"
                }`}
              >
                <Icon className="w-4 h-4" /> {t.label}
                {t.key === "providers" && providerPending > 0 && (
                  <span className="ml-1 text-[10px] bg-[#FF5E7E] text-white rounded-full px-1.5 py-0.5" data-testid="provider-pending-badge">{providerPending}</span>
                )}
                {t.key === "hosts" && hostPending > 0 && (
                  <span className="ml-1 text-[10px] bg-[#FF5E7E] text-white rounded-full px-1.5 py-0.5" data-testid="host-pending-badge">{hostPending}</span>
                )}
                {t.key === "tours" && tourPending > 0 && (
                  <span className="ml-1 text-[10px] bg-[#FF5E7E] text-white rounded-full px-1.5 py-0.5" data-testid="tour-pending-badge">{tourPending}</span>
                )}
                {t.key === "moderation" && modCount > 0 && (
                  <span className="ml-1 text-[10px] bg-[#FF5E7E] text-white rounded-full px-1.5 py-0.5" data-testid="mod-badge">{modCount}</span>
                )}
                {t.key === "car-providers" && carSummary.pending_providers > 0 && (
                  <span className="ml-1 text-[10px] bg-[#FF5E7E] text-white rounded-full px-1.5 py-0.5" data-testid="car-provider-badge">{carSummary.pending_providers}</span>
                )}
                {t.key === "vehicles" && carSummary.pending_cars > 0 && (
                  <span className="ml-1 text-[10px] bg-[#FF5E7E] text-white rounded-full px-1.5 py-0.5" data-testid="vehicle-badge">{carSummary.pending_cars}</span>
                )}
              </button>
            );
          })}
        </div>

        {tab === "dashboard" && <AdminDashboard onNav={changeTab} />}
        {tab === "users" && <UserDirectory />}
        {tab === "providers" && <AllProviders onChange={loadSummary} />}
        {tab === "hosts" && <HostApprovals onChange={loadSummary} />}
        {tab === "agents" && <AgentManagement />}
        {tab === "tours" && <TourManagement onChange={loadSummary} initialSub={searchParams.get("sub")} />}
        {tab === "bookings" && <BookingOverrides focusBooking={focusBooking} />}
        {tab === "messages" && <MessagingHub />}
        {tab === "broadcast" && <Broadcast />}
        {tab === "create" && <CreateContent onDone={() => setTab("content")} />}
        {tab === "content" && <ContentLibrary />}
        {tab === "moderation" && <Moderation onChange={loadSummary} />}
        {tab === "keywords" && <Keywords />}
        {tab === "car-providers" && <CarProviders onChange={loadSummary} />}
        {tab === "vehicles" && <VehiclesQueue onChange={loadSummary} />}
      </div>
      {profileUid && <UserProfile uid={profileUid} onClose={closeProfile} onChange={loadSummary} />}
    </div>
  );
}

function CreateContent({ onDone, editing, onCancel }) {
  const [form, setForm] = useState(editing ? {
    ...editing, tags: (editing.tags || []).join(", "),
  } : EMPTY);
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      type: form.type,
      title: form.title,
      excerpt: form.excerpt,
      body: form.body,
      cover_image: form.cover_image,
      video_url: form.video_url,
      published: form.published,
      tags: form.tags.split(",").map((t) => t.trim()).filter(Boolean),
    };
    try {
      if (editing) {
        await api.put(`/admin/blog/${editing.id}`, payload);
        toast.success("Post updated");
        onCancel();
      } else {
        await api.post("/admin/blog", payload);
        toast.success("Post published");
        setForm(EMPTY);
        onDone && onDone();
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="glass rounded-2xl p-6 max-w-2xl space-y-5" data-testid="admin-content-form">
      <div className="flex gap-3">
        {["article", "video"].map((t) => (
          <button
            key={t}
            type="button"
            data-testid={`content-type-${t}`}
            onClick={() => set("type", t)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
              form.type === t ? "btn-neon" : "glass text-slate-300"
            }`}
          >
            {t === "article" ? <FileText className="w-4 h-4" /> : <Play className="w-4 h-4" />}
            {t === "article" ? "Blog Post" : "Video Vlog"}
          </button>
        ))}
      </div>

      <Field label="Title">
        <input data-testid="content-title" value={form.title} onChange={(e) => set("title", e.target.value)} required className="input" placeholder="Post title" />
      </Field>

      {form.type === "video" && (
        <Field label="Video URL (YouTube, Vimeo, or .mp4)">
          <input data-testid="content-video-url" value={form.video_url} onChange={(e) => set("video_url", e.target.value)} className="input" placeholder="https://www.youtube.com/watch?v=..." />
        </Field>
      )}

      <Field label={form.type === "video" ? "Thumbnail image URL" : "Cover image URL"}>
        <input data-testid="content-cover" value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} className="input" placeholder="https://..." />
      </Field>

      <Field label="Excerpt (shown on cards)">
        <input data-testid="content-excerpt" value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} className="input" placeholder="Short summary" />
      </Field>

      <Field label="Body">
        <textarea data-testid="content-body" value={form.body} onChange={(e) => set("body", e.target.value)} rows={6} className="input resize-none" placeholder="Write your content..." />
      </Field>

      <Field label="Tags (comma separated)">
        <input data-testid="content-tags" value={form.tags} onChange={(e) => set("tags", e.target.value)} className="input" placeholder="Grenada, Tips, Vlog" />
      </Field>

      <label className="flex items-center gap-2 text-sm text-slate-300">
        <input type="checkbox" data-testid="content-published" checked={form.published} onChange={(e) => set("published", e.target.checked)} className="accent-cyan-400 w-4 h-4" />
        Published (visible on /blog)
      </label>

      <div className="flex gap-3">
        <button data-testid="content-save" disabled={saving} className="px-6 py-3 rounded-xl btn-neon font-semibold flex items-center gap-2 disabled:opacity-50">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlusCircle className="w-4 h-4" />}
          {editing ? "Save changes" : "Publish"}
        </button>
        {editing && <button type="button" onClick={onCancel} className="px-6 py-3 rounded-xl glass text-slate-300">Cancel</button>}
      </div>

      <style>{`.input{width:100%;background:#0B1020;border:1px solid #232A4D;border-radius:0.75rem;padding:0.75rem 1rem;font-size:0.875rem;color:#fff}.input::placeholder{color:#64748b}.input:focus{outline:none;border-color:rgba(34,211,238,0.6)}`}</style>
    </form>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-mono-caption uppercase tracking-widest text-slate-400 mb-2">{label}</label>
      {children}
    </div>
  );
}

function ContentLibrary() {
  const [posts, setPosts] = useState(null);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/blog").then(({ data }) => setPosts(data));
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    try {
      await api.delete(`/admin/blog/${id}`);
      toast.success("Post deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (editing) return <CreateContent editing={editing} onCancel={() => { setEditing(null); load(); }} />;
  if (posts === null) return <Loading />;
  if (posts.length === 0) return <Empty text="No content yet. Create your first post." />;

  return (
    <div className="space-y-3" data-testid="content-library">
      {posts.map((p) => (
        <div key={p.id} className="glass rounded-xl p-4 flex items-center gap-4" data-testid={`content-item-${p.id}`}>
          <div className="w-16 h-16 rounded-lg overflow-hidden bg-[#0B1020] shrink-0">
            {p.cover_image && <img src={p.cover_image} alt="" className="w-full h-full object-cover" />}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="text-[10px] uppercase tracking-widest text-cyan-300">{p.type}</span>
              {!p.published && <span className="text-[10px] uppercase tracking-widest text-amber-400">draft</span>}
            </div>
            <h3 className="font-semibold text-white truncate">{p.title}</h3>
            <p className="text-xs text-slate-500">{p.score} pts · {p.comments_count || 0} comments</p>
          </div>
          <Link to={`/blog/${p.id}`} target="_blank" className="p-2 text-slate-400 hover:text-cyan-400"><ExternalLink className="w-4 h-4" /></Link>
          <button data-testid={`edit-content-${p.id}`} onClick={() => setEditing(p)} className="p-2 text-slate-400 hover:text-cyan-400"><Pencil className="w-4 h-4" /></button>
          <button data-testid={`delete-content-${p.id}`} onClick={() => del(p.id)} className="p-2 text-slate-400 hover:text-[#FF5E7E]"><Trash2 className="w-4 h-4" /></button>
        </div>
      ))}
    </div>
  );
}

function Moderation({ onChange }) {
  const [threads, setThreads] = useState(null);
  const [comments, setComments] = useState([]);

  const load = () => {
    api.get("/admin/moderation/threads").then(({ data }) => setThreads(data));
    api.get("/admin/moderation/comments").then(({ data }) => setComments(data));
  };
  useEffect(() => { load(); }, []);

  const act = async (kind, id, action) => {
    try {
      await api.post(`/admin/moderation/${kind}/${id}/${action}`);
      toast.success(`Item ${action}d`);
      load();
      onChange && onChange();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  if (threads === null) return <Loading />;

  const empty = threads.length === 0 && comments.length === 0;
  if (empty) return <Empty text="Nothing to review. The queue is clear." />;

  return (
    <div className="space-y-8" data-testid="moderation-queue">
      {threads.length > 0 && (
        <div>
          <h2 className="font-heading font-bold text-white text-lg mb-3 flex items-center gap-2"><Clock className="w-4 h-4 text-amber-400" /> Threads awaiting review</h2>
          <div className="space-y-3">
            {threads.map((t) => (
              <div key={t.id} className="glass rounded-xl p-4" data-testid={`mod-thread-${t.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${t.status === "blocked" ? "text-[#FF5E7E] border-[#FF5E7E]/30" : "text-amber-400 border-amber-400/30"}`}>{t.status}</span>
                  <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full glass text-fuchsia-400">{t.category}</span>
                  {t.flagged_reason && (
                    <span className="text-xs text-[#FF5E7E] flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> flagged: "{t.flagged_reason}"</span>
                  )}
                  <span className="text-xs text-slate-500 ml-auto">{t.author_name}</span>
                </div>
                <h3 className="font-semibold text-white">{t.title}</h3>
                {t.body && <p className="text-sm text-slate-400 mt-1 line-clamp-3">{t.body}</p>}
                <div className="flex gap-2 mt-3">
                  <button data-testid={`approve-thread-${t.id}`} onClick={() => act("threads", t.id, "approve")} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 transition-colors">
                    <Check className="w-4 h-4" /> Approve
                  </button>
                  <button data-testid={`reject-thread-${t.id}`} onClick={() => act("threads", t.id, "reject")} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF5E7E]/15 text-[#FF5E7E] hover:bg-[#FF5E7E]/25 transition-colors">
                    <X className="w-4 h-4" /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {comments.length > 0 && (
        <div>
          <h2 className="font-heading font-bold text-white text-lg mb-3 flex items-center gap-2"><Ban className="w-4 h-4 text-[#FF5E7E]" /> Blocked comments</h2>
          <div className="space-y-3">
            {comments.map((c) => (
              <div key={c.id} className="glass rounded-xl p-4" data-testid={`mod-comment-${c.id}`}>
                <div className="flex items-center gap-2 mb-2">
                  {c.flagged_reason && <span className="text-xs text-[#FF5E7E] flex items-center gap-1"><AlertTriangle className="w-3.5 h-3.5" /> flagged: "{c.flagged_reason}"</span>}
                  <span className="text-xs text-slate-500 ml-auto">{c.author_name} · on {c.parent_type}</span>
                </div>
                <p className="text-sm text-slate-300">{c.body}</p>
                <div className="flex gap-2 mt-3">
                  <button data-testid={`approve-comment-${c.id}`} onClick={() => act("comments", c.id, "approve")} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25 transition-colors">
                    <Check className="w-4 h-4" /> Approve
                  </button>
                  <button data-testid={`reject-comment-${c.id}`} onClick={() => act("comments", c.id, "reject")} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF5E7E]/15 text-[#FF5E7E] hover:bg-[#FF5E7E]/25 transition-colors">
                    <X className="w-4 h-4" /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Keywords() {
  const [words, setWords] = useState(null);
  const [input, setInput] = useState("");

  const load = () => api.get("/admin/keywords").then(({ data }) => setWords(data));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
    try {
      await api.post("/admin/keywords", { word: input.trim() });
      setInput("");
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (word) => {
    try {
      await api.delete(`/admin/keywords/${encodeURIComponent(word)}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div className="max-w-2xl" data-testid="keywords-panel">
      <p className="text-sm text-slate-400 mb-4">
        Posts or comments containing any of these words are automatically blocked and sent here for review.
      </p>
      <form onSubmit={add} className="flex gap-3 mb-6">
        <input
          data-testid="keyword-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Add a flagged word..."
          className="flex-1 bg-[#0B1020] border border-[#232A4D] rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-400/60"
        />
        <button data-testid="keyword-add" className="px-5 rounded-xl btn-neon font-semibold">Add</button>
      </form>
      {words === null ? <Loading /> : (
        <div className="flex flex-wrap gap-2" data-testid="keyword-list">
          {words.map((w) => (
            <span key={w.word} data-testid={`keyword-${w.word}`} className="flex items-center gap-2 px-3 py-1.5 rounded-full glass text-sm text-slate-300">
              {w.word}
              <button data-testid={`remove-keyword-${w.word}`} onClick={() => del(w.word)} className="text-slate-500 hover:text-[#FF5E7E]"><X className="w-3.5 h-3.5" /></button>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function Loading() {
  return <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-cyan-400" /></div>;
}
function Empty({ text }) {
  return <p className="text-slate-500 text-center py-16">{text}</p>;
}

const cardCls = "glass rounded-2xl p-5";
const inputDark = "w-full bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50";

function AdminDashboard({ onNav }) {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <Loading />;
  const tiles = [
    { label: "Total Bookings", value: s.total_bookings, sub: `${s.tour_bookings} tours · ${s.car_bookings} cars`, icon: CalendarClock, color: "from-cyan-500 to-blue-600", tab: "bookings" },
    { label: "Total Members", value: s.total_members, sub: `${s.total_customers} customers`, icon: Users, color: "from-fuchsia-500 to-purple-600", tab: "users" },
    { label: "Active Providers", value: s.active_providers, sub: "Tour operators & car rentals", icon: KeyRound, color: "from-orange-500 to-pink-600", tab: "car-providers" },
  ];
  return (
    <div className="grid sm:grid-cols-3 gap-5" data-testid="admin-dashboard">
      {tiles.map((t) => (
        <button
          key={t.label}
          type="button"
          onClick={() => onNav && onNav(t.tab)}
          className={`${cardCls} group text-left cursor-pointer transition-all duration-200 hover:-translate-y-1 hover:border-cyan-400/40 hover:shadow-[0_0_28px_-6px_rgba(34,211,238,0.5)]`}
          data-testid={`stat-${t.label.replace(/\s/g, "-").toLowerCase()}`}
        >
          <div className="flex items-start justify-between">
            <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${t.color} flex items-center justify-center mb-4`}><t.icon className="w-6 h-6 text-white" /></div>
            <ExternalLink className="w-4 h-4 text-slate-600 transition-colors group-hover:text-cyan-400" />
          </div>
          <p className="font-heading text-4xl font-extrabold text-white">{t.value?.toLocaleString()}</p>
          <p className="text-sm text-slate-300 mt-1 group-hover:text-cyan-300 transition-colors">{t.label}</p>
          <p className="text-xs text-slate-500 mt-0.5">{t.sub}</p>
        </button>
      ))}
    </div>
  );
}

function UserDirectory() {
  const [users, setUsers] = useState(null);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [detail, setDetail] = useState(null);

  const load = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (role) params.role = role;
    api.get("/admin/users", { params }).then(({ data }) => setUsers(data)).catch(() => {});
  }, [q, role]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  return (
    <div data-testid="admin-users">
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <div className="flex-1 flex items-center gap-2 bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-3 py-2.5">
          <Search className="w-4 h-4 text-cyan-400" />
          <input data-testid="user-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search members by name or email…" className="bg-transparent text-white text-sm w-full outline-none" />
        </div>
        <select data-testid="user-role-filter" value={role} onChange={(e) => setRole(e.target.value)} className="bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-4 py-2.5 text-sm text-white outline-none">
          <option value="">All roles</option>
          <option value="traveler">Customers</option>
          <option value="provider">Operators</option>
          <option value="admin">Admins</option>
        </select>
      </div>
      {users === null ? <Loading /> : users.length === 0 ? <Empty text="No members found." /> : (
        <div className="glass rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="text-left text-slate-400 border-b border-[#1D2640]">
              <tr><th className="px-5 py-3 font-medium">Name</th><th className="px-5 py-3 font-medium">Email</th><th className="px-5 py-3 font-medium">Role</th><th className="px-5 py-3 font-medium text-right">Points</th><th className="px-5 py-3 font-medium text-right">Commission</th></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id} data-testid={`user-row-${u.id}`} onClick={() => setDetail(u.id)} className="group border-b border-[#1D2640]/50 hover:bg-cyan-400/5 cursor-pointer transition-colors">
                  <td className="px-5 py-3 text-white font-medium group-hover:text-cyan-400 transition-colors">{u.name}</td>
                  <td className="px-5 py-3 text-slate-400">{u.email}</td>
                  <td className="px-5 py-3"><span className="text-xs px-2 py-0.5 rounded-full glass text-slate-300 capitalize">{u.role}</span></td>
                  <td className="px-5 py-3 text-right text-cyan-300">{(u.points || 0).toLocaleString()}</td>
                  <td className="px-5 py-3 text-right text-slate-300">{u.role === "provider" ? `${Math.round((u.commission_rate || 0.2) * 100)}%` : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {detail && <UserProfile uid={detail} onClose={() => setDetail(null)} onChange={load} />}
    </div>
  );
}

function UserProfile({ uid, onClose, onChange }) {
  const [data, setData] = useState(null);
  const [pts, setPts] = useState("");
  const [reason, setReason] = useState("");
  const [comm, setComm] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "" });
  const [busy, setBusy] = useState(false);
  const [tempPw, setTempPw] = useState(null);

  const load = useCallback(() => api.get(`/admin/users/${uid}`).then(({ data }) => {
    setData(data);
    setComm(String(Math.round((data.user.commission_rate || 0.2) * 100)));
    setForm({ name: data.user.name || "", email: data.user.email || "", role: data.user.role || "traveler" });
  }), [uid]);
  useEffect(() => { load(); }, [load]);

  const adjust = async () => {
    const delta = parseInt(pts, 10);
    if (!delta) { toast.error("Enter a non-zero amount"); return; }
    try { await api.post(`/admin/users/${uid}/wallet`, { delta, reason }); toast.success("Wallet updated"); setPts(""); setReason(""); load(); onChange && onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const saveComm = async () => {
    try { await api.post(`/admin/users/${uid}/commission`, { rate: Number(comm) }); toast.success("Commission updated"); load(); onChange && onChange(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveEdit = async () => {
    setBusy(true);
    try {
      await api.put(`/admin/users/${uid}`, { name: form.name, email: form.email, role: form.role });
      toast.success("User details updated");
      setEditing(false); load(); onChange && onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const toggleActive = async () => {
    const next = !(u.is_active !== false);
    setBusy(true);
    try {
      await api.post(`/admin/users/${uid}/active`, { active: next });
      toast.success(next ? "Account reactivated" : "Account deactivated — the user is now signed out");
      load(); onChange && onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const del = async () => {
    if (!window.confirm(`Permanently delete ${u.name} and ALL their data? This cannot be undone.`)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/users/${uid}`);
      toast.success("User permanently deleted");
      onClose(); onChange && onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBusy(false); }
  };
  const resetPassword = async () => {
    if (!window.confirm(`Generate a new temporary password for ${u.name}? It will be emailed to them immediately.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${uid}/reset-password`);
      setTempPw(data.temp_password);
      toast.success(`Temporary password emailed to ${data.email}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const approveHost = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/hosts/${uid}/approve`);
      toast.success("Host approved — they can now publish listings");
      load(); onChange && onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const u = data?.user;
  const active = u && u.is_active !== false;
  const hostStatus = !u ? null : (
    [u.host_status, u.provider_status, u.car_rental_profile?.status].includes("rejected") ? "rejected"
    : (u.host_status === "pending" || u.provider_status === "pending" || ["pending", "submitted"].includes(u.car_rental_profile?.status)) ? "pending"
    : "approved"
  );
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" onClick={onClose}>
      <div className="glass neon-border rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()} data-testid="user-profile">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading text-xl font-bold text-white">Member profile</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        {!u ? <Loading /> : (
          <>
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white font-bold text-lg">{u.name?.[0]?.toUpperCase()}</div>
              <div className="flex-1 min-w-0"><p className="font-semibold text-white">{u.name}</p><p className="text-xs text-slate-400 truncate">{u.email} · <span className="capitalize">{u.role}</span></p></div>
            </div>
            <div className="flex flex-wrap gap-2 mb-4" data-testid="user-status-badges">
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${active ? "text-emerald-400 border-emerald-400/30" : "text-[#FF5E7E] border-[#FF5E7E]/30"}`}>{active ? "Active" : "Deactivated"}</span>
              <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${u.email_verified ? "text-cyan-400 border-cyan-400/30" : "text-amber-400 border-amber-400/30"}`}>{u.email_verified ? "Email verified" : "Unverified"}</span>
              {u.role === "provider" && (
                <span data-testid="host-status-badge" className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${hostStatus === "approved" ? "text-emerald-400 border-emerald-400/30" : hostStatus === "rejected" ? "text-[#FF5E7E] border-[#FF5E7E]/30" : "text-amber-400 border-amber-400/30"}`}>{hostStatus === "pending" ? "Pending Provider" : hostStatus === "approved" ? "Provider" : `Host ${hostStatus}`}</span>
              )}
            </div>

            {/* USER MANAGEMENT CONTROLS */}
            <div className="glass rounded-xl p-4 mb-4" data-testid="user-management-controls">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-semibold text-white flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-fuchsia-400" /> User management</p>
                {!editing && (
                  <button data-testid="edit-user-btn" onClick={() => setEditing(true)} className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg glass text-cyan-300 hover:text-white transition-colors"><Pencil className="w-3.5 h-3.5" /> Edit User</button>
                )}
              </div>

              {editing ? (
                <div className="space-y-2.5" data-testid="edit-user-form">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-slate-400">Name</label>
                    <input data-testid="edit-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputDark} />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-slate-400">Email</label>
                    <input data-testid="edit-user-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputDark} />
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-slate-400">Role</label>
                    <select data-testid="edit-user-role" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className={inputDark}>
                      <option value="traveler" className="bg-[#0B0E1B]">Traveler</option>
                      <option value="provider" className="bg-[#0B0E1B]">Provider</option>
                      <option value="admin" className="bg-[#0B0E1B]">Admin</option>
                    </select>
                  </div>
                  <div className="flex gap-2 pt-1">
                    <button data-testid="save-user-btn" disabled={busy} onClick={saveEdit} className="btn-neon rounded-xl px-4 py-2 text-sm font-semibold disabled:opacity-50">Save changes</button>
                    <button onClick={() => { setEditing(false); load(); }} className="glass rounded-xl px-4 py-2 text-sm text-slate-300">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col sm:flex-row gap-2.5">
                  <button data-testid="toggle-active-btn" onClick={toggleActive} disabled={busy}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-colors disabled:opacity-50 ${active ? "bg-[#FF5E7E]/15 text-[#FF5E7E] hover:bg-[#FF5E7E]/25" : "bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"}`}>
                    {active ? <><Ban className="w-4 h-4" /> Deactivate Account</> : <><Check className="w-4 h-4" /> Reactivate Account</>}
                  </button>
                  <button data-testid="delete-user-btn" onClick={del} disabled={busy}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-[#FF5E7E]/10 text-[#FF5E7E] border border-[#FF5E7E]/30 hover:bg-[#FF5E7E]/25 transition-colors disabled:opacity-50">
                    <Trash2 className="w-4 h-4" /> Delete User
                  </button>
                </div>
              )}

              {!editing && (
                <div className="mt-2.5">
                  <button data-testid="reset-password-btn" onClick={resetPassword} disabled={busy}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-cyan-500/10 text-cyan-300 border border-cyan-400/30 hover:bg-cyan-500/20 transition-colors disabled:opacity-50">
                    <KeyRound className="w-4 h-4" /> Reset User Password
                  </button>
                  {tempPw && (
                    <div className="mt-3 rounded-xl border border-cyan-400/40 bg-[#060810] p-3.5" data-testid="temp-password-panel">
                      <p className="text-[10px] uppercase tracking-widest text-slate-400 mb-1.5">Temporary password (emailed to user)</p>
                      <div className="flex items-center gap-2">
                        <code data-testid="temp-password-value" className="flex-1 text-cyan-300 font-mono-caption text-lg tracking-[0.25em] font-bold">{tempPw}</code>
                        <button data-testid="copy-temp-password" onClick={() => { navigator.clipboard?.writeText(tempPw); toast.success("Copied"); }}
                          className="text-xs px-3 py-1.5 rounded-lg glass text-slate-300 hover:text-white">Copy</button>
                      </div>
                      <p className="text-[11px] text-slate-500 mt-2">Share only if the email doesn't arrive. The user should change it immediately after logging in.</p>
                    </div>
                  )}
                </div>
              )}
              {!editing && u.role === "provider" && hostStatus !== "approved" && (
                <button data-testid="approve-host-modal-btn" onClick={approveHost} disabled={busy}
                  className="mt-2.5 w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold bg-emerald-500/15 text-emerald-400 border border-emerald-400/30 hover:bg-emerald-500/25 transition-colors disabled:opacity-50">
                  <ShieldCheck className="w-4 h-4" /> Approve Host
                </button>
              )}
            </div>

            <div className="grid grid-cols-3 gap-2 mb-5 text-center">
              <Stat label="Trips" value={data.stats.tour_bookings + data.stats.car_bookings} />
              <Stat label="Listings" value={data.stats.listings + data.stats.vehicles} />
              <Stat label="As provider" value={data.stats.provider_bookings} />
            </div>

            <div className="glass rounded-xl p-4 mb-4">
              <p className="text-sm font-semibold text-white flex items-center gap-2 mb-1"><Wallet className="w-4 h-4 text-cyan-400" /> Wallet</p>
              <p className="text-2xl font-heading font-extrabold text-cyan-300 mb-3" data-testid="profile-points">{(u.points || 0).toLocaleString()} pts</p>
              <div className="flex gap-2">
                <input data-testid="wallet-delta" type="number" value={pts} onChange={(e) => setPts(e.target.value)} placeholder="+/- points" className={inputDark} />
                <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" className={inputDark} />
                <button data-testid="wallet-adjust-btn" onClick={adjust} className="btn-neon rounded-xl px-4 py-2 text-sm font-semibold whitespace-nowrap">Adjust</button>
              </div>
            </div>

            {u.role === "provider" && (
              <div className="glass rounded-xl p-4">
                <p className="text-sm font-semibold text-white flex items-center gap-2 mb-1"><Percent className="w-4 h-4 text-orange-400" /> Custom commission</p>
                <p className="text-xs text-slate-400 mb-3">Default 20%. All new bookings for this provider use this rate.</p>
                <div className="flex gap-2 items-center">
                  <div className="flex items-center gap-1 bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-3 py-2.5 flex-1">
                    <input data-testid="commission-input" type="number" min={0} max={90} value={comm} onChange={(e) => setComm(e.target.value)} className="bg-transparent text-white text-sm w-full outline-none" />
                    <span className="text-slate-400 text-sm">%</span>
                  </div>
                  <button data-testid="commission-save-btn" onClick={saveComm} className="btn-cool rounded-xl px-4 py-2 text-sm font-semibold">Save</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return <div className="glass rounded-xl py-2"><p className="text-lg font-bold text-white">{value}</p><p className="text-[10px] uppercase tracking-wider text-slate-500">{label}</p></div>;
}

function AllProviders({ onChange }) {
  const [providers, setProviders] = useState(null);
  const [filter, setFilter] = useState("");

  const load = useCallback(() => {
    api.get("/admin/providers", { params: filter ? { status: filter } : {} })
      .then(({ data }) => setProviders(data)).catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const act = async (uid, action) => {
    try {
      await api.post(`/admin/providers/${uid}/${action}`);
      toast.success(action === "approve" ? "Provider approved & verified" : "Provider rejected");
      load(); onChange && onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const forceApprove = async (uid) => {
    if (!window.confirm("Force Approve this provider now? They'll be marked Active & Verified even without uploaded documents.")) return;
    try {
      await api.post(`/admin/providers/${uid}/force-approve`);
      toast.success("Provider force-approved — marked Active & Admin Verified");
      load(); onChange && onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const STATUS = {
    pending: { cls: "text-amber-400 border-amber-400/30", label: "Pending Provider" },
    approved: { cls: "text-emerald-400 border-emerald-400/30", label: "Approved" },
    rejected: { cls: "text-[#FF5E7E] border-[#FF5E7E]/30", label: "Rejected" },
  };
  const CAT_CLS = { "Tours": "text-cyan-300", "Car Rental": "text-fuchsia-300", "Both": "text-orange-300" };

  return (
    <div data-testid="admin-all-providers">
      <p className="text-sm text-slate-400 mb-4">Every host who signs up — tour operators, car rentals or both — appears here. Approve to move them out of verification.</p>
      <div className="flex gap-2 mb-4">
        {[{ v: "", l: "All" }, { v: "pending", l: "Verification in progress" }, { v: "approved", l: "Approved" }, { v: "rejected", l: "Rejected" }].map((f) => (
          <button key={f.v || "all"} data-testid={`provider-filter-${f.v || "all"}`} onClick={() => setFilter(f.v)}
            className={`text-xs px-3 py-1.5 rounded-full ${filter === f.v ? "btn-neon font-semibold" : "glass text-slate-300"}`}>{f.l}</button>
        ))}
      </div>
      {providers === null ? <Loading /> : providers.length === 0 ? <Empty text="No providers in this view." /> : (
        <div className="space-y-3">
          {providers.map((p) => {
            const s = STATUS[p.verification_status] || STATUS.pending;
            return (
              <div key={p.id} className="glass rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`provider-row-${p.id}`}>
                <div className="w-11 h-11 rounded-full overflow-hidden bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shrink-0">
                  {p.avatar_url ? <img src={fileUrl(p.avatar_url)} alt="" className="w-full h-full object-cover" /> : <span className="text-white font-bold">{p.name?.[0]?.toUpperCase()}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-white">{p.name}</h3>
                    {p.admin_verified && <AdminVerifiedBadge showLabel={false} />}
                    <span data-testid={`provider-status-${p.id}`} className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${s.cls}`}>{s.label}</span>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full glass ${CAT_CLS[p.category] || "text-slate-300"}`}>{p.category}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">{p.email} · {p.listings_count} tour{p.listings_count === 1 ? "" : "s"}</p>
                </div>
                <div className="flex gap-2 shrink-0 flex-wrap">
                  <Link to={`/admin/hosts/${p.id}/verify`} data-testid={`provider-view-${p.id}`} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold glass text-cyan-300 hover:text-white"><FileText className="w-4 h-4" /> Documents</Link>
                  {p.verification_status !== "approved" && (
                    <>
                      <button data-testid={`approve-provider-${p.id}`} onClick={() => act(p.id, "approve")} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"><Check className="w-4 h-4" /> Approve</button>
                      <button data-testid={`force-approve-provider-${p.id}`} onClick={() => forceApprove(p.id)} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-cyan-500 to-blue-600 text-white hover:opacity-90"><BadgeCheck className="w-4 h-4" /> Force Approve</button>
                    </>
                  )}
                  {p.verification_status !== "rejected" && (
                    <button data-testid={`reject-provider-${p.id}`} onClick={() => act(p.id, "reject")} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF5E7E]/15 text-[#FF5E7E] hover:bg-[#FF5E7E]/25"><X className="w-4 h-4" /> Reject</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function HostApprovals({ onChange }) {
  const [hosts, setHosts] = useState(null);
  const [filter, setFilter] = useState("pending");

  const load = useCallback(() => {
    api.get("/admin/host-approvals", { params: filter ? { status: filter } : {} })
      .then(({ data }) => setHosts(data)).catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load]);

  const act = async (uid, action) => {
    try {
      await api.post(`/admin/hosts/${uid}/${action}`);
      toast.success(`Host ${action}d`);
      load(); onChange && onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const verifyEmail = async (uid) => {
    try {
      await api.post(`/admin/users/${uid}/verify-email`);
      toast.success("Email manually verified — host unlocked");
      load(); onChange && onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const STATUS_CLS = {
    pending: "text-amber-400 border-amber-400/30",
    approved: "text-emerald-400 border-emerald-400/30",
    rejected: "text-[#FF5E7E] border-[#FF5E7E]/30",
  };

  return (
    <div data-testid="admin-host-approvals">
      <p className="text-sm text-slate-400 mb-4">Review providers who signed up and approve or reject their host account. Only approved hosts can publish tours.</p>
      <div className="flex gap-2 mb-4">
        {["pending", "approved", "rejected", ""].map((f) => (
          <button key={f || "all"} data-testid={`host-filter-${f || "all"}`} onClick={() => setFilter(f)}
            className={`text-xs px-3 py-1.5 rounded-full capitalize ${filter === f ? "btn-neon font-semibold" : "glass text-slate-300"}`}>
            {f || "all"}
          </button>
        ))}
      </div>
      {hosts === null ? <Loading /> : hosts.length === 0 ? <Empty text="No hosts in this queue." /> : (
        <div className="space-y-3">
          {hosts.map((h) => {
            const status = h.host_status || "approved";
            return (
              <div key={h.id} className="glass rounded-xl p-4 flex flex-col sm:flex-row sm:items-center gap-4" data-testid={`host-row-${h.id}`}>
                <div className="w-11 h-11 rounded-full overflow-hidden bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center shrink-0">
                  {h.avatar_url ? <img src={fileUrl(h.avatar_url)} alt="" className="w-full h-full object-cover" /> : <span className="text-white font-bold">{h.name?.[0]?.toUpperCase()}</span>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-white">{h.name}</h3>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${STATUS_CLS[status]}`}>{status === "pending" ? "Pending Provider" : status}</span>
                    <span data-testid={`host-email-status-${h.id}`} className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${h.email_verified ? "text-cyan-400 border-cyan-400/30" : "text-amber-400 border-amber-400/30"}`}>{h.email_verified ? "Email verified" : "Email pending"}</span>
                    <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full glass text-slate-300">{(h.provider_types || []).join(", ") || "tour_operator"}</span>
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">{h.email} · {h.listings_count} tour{h.listings_count === 1 ? "" : "s"}</p>
                  {h.bio && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{h.bio}</p>}
                </div>
                <div className="flex gap-2 shrink-0 flex-wrap">
                  {!h.email_verified && (
                    <button data-testid={`verify-email-${h.id}`} onClick={() => verifyEmail(h.id)} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-cyan-500/15 text-cyan-300 hover:bg-cyan-500/25"><MailCheck className="w-4 h-4" /> Manually Verify Email</button>
                  )}
                  {status !== "approved" && (
                    <button data-testid={`approve-host-${h.id}`} onClick={() => act(h.id, "approve")} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"><Check className="w-4 h-4" /> Approve</button>
                  )}
                  {status !== "rejected" && (
                    <button data-testid={`reject-host-${h.id}`} onClick={() => act(h.id, "reject")} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF5E7E]/15 text-[#FF5E7E] hover:bg-[#FF5E7E]/25"><X className="w-4 h-4" /> Reject</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function BookingOverrides({ focusBooking }) {
  const [bookings, setBookings] = useState(null);
  const [q, setQ] = useState("");
  const [edit, setEdit] = useState(null);
  const [chat, setChat] = useState(null);

  const load = useCallback(() => api.get("/admin/bookings", { params: q ? { q } : {} }).then(({ data }) => setBookings(data)), [q]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);

  // Auto-open the booking targeted by a payout notification deep-link.
  useEffect(() => {
    if (focusBooking && bookings) {
      const b = bookings.find((x) => x.id === focusBooking);
      if (b) setEdit({ id: b.id, date: b.date || b.pickup_date || "", time: b.time || "", status: b.status || "" });
    }
  }, [focusBooking, bookings]);

  const save = async () => {
    try { await api.put(`/admin/bookings/${edit.id}`, { date: edit.date, time: edit.time, status: edit.status }); toast.success("Booking updated"); setEdit(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-bookings">
      <div className="flex items-center gap-2 bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-3 py-2.5 mb-4 max-w-md">
        <Search className="w-4 h-4 text-cyan-400" />
        <input data-testid="booking-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by tour, traveler or provider…" className="bg-transparent text-white text-sm w-full outline-none" />
      </div>
      {bookings === null ? <Loading /> : bookings.length === 0 ? <Empty text="No bookings." /> : (
        <div className="glass rounded-2xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead className="text-left text-slate-400 border-b border-[#1D2640]">
              <tr><th className="px-4 py-3 font-medium">Item</th><th className="px-4 py-3 font-medium">Traveler</th><th className="px-4 py-3 font-medium">Date</th><th className="px-4 py-3 font-medium">Status</th><th className="px-4 py-3 font-medium text-right">Actions</th></tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id} data-testid={`admin-booking-${b.id}`} onClick={() => setEdit({ id: b.id, date: b.date || b.pickup_date || "", time: b.time || "", status: b.status || "" })} className="group border-b border-[#1D2640]/50 hover:bg-cyan-400/5 cursor-pointer transition-colors">
                  <td className="px-4 py-3 text-white group-hover:text-cyan-400 transition-colors">{b.title} <span className="text-[10px] uppercase text-slate-500">{b.kind}</span></td>
                  <td className="px-4 py-3 text-slate-400">{b.traveler_name}</td>
                  <td className="px-4 py-3 text-slate-400 text-xs">{b.date || b.pickup_date} {b.time || ""}</td>
                  <td className="px-4 py-3"><span className="text-xs px-2 py-0.5 rounded-full glass text-slate-300 capitalize">{(b.status || "").replace("_", " ")}</span></td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button data-testid={`edit-booking-${b.id}`} onClick={(e) => { e.stopPropagation(); setEdit({ id: b.id, date: b.date || b.pickup_date || "", time: b.time || "", status: b.status || "" }); }} className="text-xs px-3 py-1.5 rounded-lg glass text-cyan-300 hover:text-white mr-2">Override</button>
                    <button data-testid={`chat-booking-${b.id}`} onClick={(e) => { e.stopPropagation(); setChat(b.id); }} className="text-xs px-3 py-1.5 rounded-lg glass text-fuchsia-300 hover:text-white">Chat</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {edit && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/75 backdrop-blur-sm" onClick={() => setEdit(null)}>
          <div className="glass neon-border rounded-2xl w-full max-w-sm p-6" onClick={(e) => e.stopPropagation()} data-testid="override-modal">
            <h2 className="font-heading text-lg font-bold text-white mb-4">Override booking</h2>
            <label className="text-xs text-slate-400">Date</label>
            <input type="date" value={edit.date} onChange={(e) => setEdit({ ...edit, date: e.target.value })} className={inputDark + " mb-3"} data-testid="override-date" />
            <label className="text-xs text-slate-400">Time</label>
            <input value={edit.time} onChange={(e) => setEdit({ ...edit, time: e.target.value })} placeholder="09:00" className={inputDark + " mb-3"} data-testid="override-time" />
            <label className="text-xs text-slate-400">Status</label>
            <select value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} className={inputDark + " mb-4"} data-testid="override-status">
              {["pending", "confirmed", "cancelled", "completed", "picked_up", "returned"].map((s) => <option key={s} value={s} className="bg-[#0B0E1B]">{s}</option>)}
            </select>
            <button onClick={save} data-testid="override-save" className="w-full btn-neon rounded-xl py-2.5 font-semibold">Save changes</button>
          </div>
        </div>
      )}
      {chat && <TripChat bookingId={chat} onClose={() => setChat(null)} />}
    </div>
  );
}

function MessagingHub() {
  const [threads, setThreads] = useState(null);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/admin/trip-chats").then(({ data }) => setThreads(data)).catch(() => {}); }, [open]);
  if (threads === null) return <Loading />;
  return (
    <div data-testid="admin-messages">
      <p className="text-sm text-slate-400 mb-4">Monitor and join active trip chats between guests and providers.</p>
      {threads.length === 0 ? <Empty text="No trip chats yet." /> : (
        <div className="space-y-3">
          {threads.map((t) => (
            <button key={t.booking_id} data-testid={`thread-${t.booking_id}`} onClick={() => setOpen(t.booking_id)} className="w-full text-left glass glass-hover rounded-2xl p-4 flex items-center gap-4">
              <div className="w-10 h-10 rounded-full bg-fuchsia-500/15 flex items-center justify-center"><MessageSquare className="w-5 h-5 text-fuchsia-400" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-white truncate">{t.title}</p>
                <p className="text-xs text-slate-400">{t.traveler_name} ↔ {t.provider_name} · {t.count} messages</p>
                {t.last && <p className="text-xs text-slate-500 truncate mt-0.5">{t.last.sender_name}: {t.last.text}</p>}
              </div>
            </button>
          ))}
        </div>
      )}
      {open && <TripChat bookingId={open} onClose={() => setOpen(null)} />}
    </div>
  );
}

function TourManagement({ onChange, initialSub }) {
  const [sub, setSub] = useState(initialSub === "pending" ? "pending" : "active");
  const [tours, setTours] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(() => {
    setTours(null);
    api.get("/admin/tours", { params: { status: sub } })
      .then(({ data }) => setTours(data))
      .catch(() => setTours([]));
  }, [sub]);
  useEffect(() => { load(); }, [load]);

  const act = async (id, action) => {
    try {
      await api.post(`/admin/tours/${id}/${action}`);
      toast.success(action === "approve" ? "Tour approved — now live" : "Tour rejected");
      setPreview(null); load(); onChange && onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const SUBS = [
    { key: "active", label: "Active Tours" },
    { key: "pending", label: "Pending Approval" },
  ];

  return (
    <div data-testid="admin-tour-management">
      <div className="flex items-center gap-2 mb-5">
        <MapPin className="w-5 h-5 text-cyan-400" />
        <h2 className="font-heading text-xl font-bold text-white">Tour Management</h2>
      </div>

      <div className="inline-flex p-1 rounded-xl bg-[#0B0E1B] border border-[#1D2640] mb-6">
        {SUBS.map((s) => (
          <button key={s.key} onClick={() => setSub(s.key)} data-testid={`tour-subtab-${s.key}`}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${sub === s.key ? "bg-cyan-400 text-[#060810]" : "text-slate-400 hover:text-white"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {tours === null ? <Loading /> : tours.length === 0 ? (
        <Empty text={sub === "pending" ? "No tours awaiting approval." : "No active tours yet."} />
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tours.map((t) => (
            <div key={t.id} onClick={() => setPreview(t)} role="button" tabIndex={0}
              onKeyDown={(e) => { if (e.key === "Enter") setPreview(t); }}
              className="glass rounded-xl overflow-hidden cursor-pointer transition-all hover:ring-1 hover:ring-cyan-400/40"
              data-testid={`tour-card-${t.id}`}>
              <div className="h-36 bg-[#0B0E1B] overflow-hidden relative">
                {t.image_url ? <img src={t.image_url} alt={t.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><MapPin className="w-8 h-8 text-slate-700" /></div>}
                {sub === "pending" && <span className="absolute top-2 left-2 text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-400 text-[#060810] font-bold">Pending</span>}
              </div>
              <div className="p-3">
                <p className="text-sm font-semibold text-white truncate">{t.title}</p>
                <p className="text-xs text-slate-400 mt-0.5 truncate">{t.provider_name} · {t.island}</p>
                <div className="flex items-center justify-between mt-2">
                  <span className="text-sm text-cyan-300 font-semibold">${t.price} <span className="text-xs text-slate-500">/ {t.price_type === "per_group" ? "group" : "person"}</span></span>
                  <span className="text-xs text-slate-400 flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> Preview</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {preview && <TourPreview tour={preview} onClose={() => setPreview(null)} onApprove={() => act(preview.id, "approve")} onReject={() => act(preview.id, "reject")} showApprove={preview.status === "pending"} />}
    </div>
  );
}

function TourPreview({ tour, onClose, onApprove, onReject, showApprove }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="tour-preview-modal">
      <div className="bg-[#0B0E1B] border border-[#1D2640] rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="h-52 bg-[#060810] overflow-hidden relative">
          {tour.image_url ? <img src={tour.image_url} alt={tour.title} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center"><MapPin className="w-10 h-10 text-slate-700" /></div>}
          <button onClick={onClose} data-testid="tour-preview-close" className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-6">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h3 className="font-heading text-2xl font-extrabold text-white">{tour.title}</h3>
              <p className="text-sm text-slate-400 mt-1">by {tour.provider_name} · {tour.island} · {tour.category}</p>
            </div>
            <span className={`text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border ${tour.status === "pending" ? "text-amber-400 border-amber-400/30" : tour.status === "rejected" ? "text-[#FF5E7E] border-[#FF5E7E]/30" : "text-emerald-400 border-emerald-400/30"}`}>{tour.status || "approved"}</span>
          </div>
          <div className="flex flex-wrap gap-4 mt-4 text-sm">
            <span className="text-cyan-300 font-bold text-lg">${tour.price} <span className="text-xs text-slate-500 font-normal">/ {tour.price_type === "per_group" ? "group" : "person"}</span></span>
            {tour.duration && <span className="text-slate-400 flex items-center gap-1"><Clock className="w-4 h-4" /> {tour.duration}</span>}
            {tour.max_guests && <span className="text-slate-400 flex items-center gap-1"><Users className="w-4 h-4" /> up to {tour.max_guests}</span>}
          </div>
          {tour.description && <p className="text-slate-300 text-sm leading-relaxed mt-4 whitespace-pre-line">{tour.description}</p>}
          {tour.meeting_point && <p className="text-sm text-slate-400 mt-3"><span className="text-slate-500">Meeting point:</span> {tour.meeting_point}</p>}
          {(tour.amenities || []).length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {tour.amenities.map((a, i) => <span key={i} className="text-xs px-2.5 py-1 rounded-full bg-[#141B2E] text-slate-300">{a}</span>)}
            </div>
          )}
          {(tour.images || []).length > 1 && (
            <div className="grid grid-cols-4 gap-2 mt-4">
              {tour.images.slice(0, 8).map((img, i) => <img key={i} src={img} alt="" className="h-16 w-full object-cover rounded-lg" />)}
            </div>
          )}
          <div className="flex gap-3 mt-6 pt-5 border-t border-[#1D2640]">
            {showApprove ? (
              <>
                <button onClick={onApprove} data-testid="tour-approve-btn" className="flex-1 btn-neon rounded-xl px-6 py-3 font-bold flex items-center justify-center gap-2"><Check className="w-5 h-5" /> Approve Tour</button>
                <button onClick={onReject} data-testid="tour-reject-btn" className="px-5 py-3 rounded-xl text-sm font-semibold bg-[#FF5E7E]/15 text-[#FF5E7E] hover:bg-[#FF5E7E]/25 flex items-center gap-2"><X className="w-4 h-4" /> Reject</button>
              </>
            ) : (
              <>
                <a href={`/listing/${tour.id}`} target="_blank" rel="noreferrer" className="flex-1 text-center rounded-xl px-6 py-3 text-sm font-semibold glass text-cyan-300 hover:text-white flex items-center justify-center gap-2"><ExternalLink className="w-4 h-4" /> View live listing</a>
                <button onClick={onReject} data-testid="tour-unpublish-btn" className="px-5 py-3 rounded-xl text-sm font-semibold bg-[#FF5E7E]/15 text-[#FF5E7E] hover:bg-[#FF5E7E]/25 flex items-center gap-2"><Ban className="w-4 h-4" /> Unpublish</button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}


function Broadcast() {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [email, setEmail] = useState(false);
  const [audience, setAudience] = useState("all");
  const [sending, setSending] = useState(false);

  const AUDIENCES = [
    { value: "all", label: "All members" },
    { value: "provider", label: "Providers only" },
    { value: "traveler", label: "Travelers only" },
  ];

  const send = async () => {
    if (!title.trim() || !body.trim()) { toast.error("Add a title and message"); return; }
    setSending(true);
    try { const { data } = await api.post("/admin/broadcast", { title, body, email, audience }); toast.success(`Broadcast sent to ${data.sent} members${email ? " + email" : ""}`); setTitle(""); setBody(""); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-xl" data-testid="admin-broadcast">
      <div className="flex items-center gap-2 mb-4"><Megaphone className="w-5 h-5 text-orange-400" /><h2 className="font-heading text-xl font-bold text-white">Global broadcast</h2></div>
      <p className="text-sm text-slate-400 mb-5">Send an in-app notification (and optionally an email) to your chosen audience.</p>
      <label className="text-xs text-slate-400">Audience</label>
      <select data-testid="broadcast-audience" value={audience} onChange={(e) => setAudience(e.target.value)} className={inputDark + " mb-3"}>
        {AUDIENCES.map((a) => <option key={a.value} value={a.value}>{a.label}</option>)}
      </select>
      <label className="text-xs text-slate-400">Title</label>
      <input data-testid="broadcast-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="New excursions just landed!" className={inputDark + " mb-3"} />
      <label className="text-xs text-slate-400">Message</label>
      <textarea data-testid="broadcast-body" value={body} onChange={(e) => setBody(e.target.value)} rows={4} placeholder="Write your announcement…" className={inputDark + " mb-3 resize-none"} />
      <label className="flex items-center gap-2 text-sm text-slate-300 mb-5 cursor-pointer">
        <input type="checkbox" data-testid="broadcast-email" checked={email} onChange={(e) => setEmail(e.target.checked)} className="accent-cyan-400 w-4 h-4" /> Also send by email
      </label>
      <button onClick={send} disabled={sending} data-testid="broadcast-send" className="btn-neon rounded-xl px-6 py-3 font-semibold flex items-center gap-2 disabled:opacity-60">
        {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send broadcast
      </button>
    </div>
  );
}

function CarProviders({ onChange }) {
  const [providers, setProviders] = useState(null);
  const navigate = useNavigate();
  const load = () => api.get("/admin/car-providers").then(({ data }) => setProviders(data));
  useEffect(() => { load(); }, []);

  const act = async (uid, action) => {
    try {
      await api.post(`/admin/car-providers/${uid}/${action}`);
      toast.success(`Provider ${action}d`);
      load(); onChange && onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (providers === null) return <Loading />;
  const applied = providers.filter((p) => p.profile && p.profile.status && p.profile.status !== "incomplete");
  if (applied.length === 0) return <Empty text="No car rental applications yet." />;

  const stop = (e) => e.stopPropagation();

  return (
    <div className="space-y-3" data-testid="admin-car-providers">
      {applied.map((p) => (
        <div key={p.id} onClick={() => navigate(`/admin/car-operators/${p.id}`)}
          role="button" tabIndex={0}
          onKeyDown={(e) => { if (e.key === "Enter") navigate(`/admin/car-operators/${p.id}`); }}
          className="glass rounded-xl p-4 cursor-pointer transition-colors hover:bg-white/[0.03] hover:ring-1 hover:ring-cyan-400/30"
          data-testid={`car-provider-${p.id}`}>
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <KeyRound className="w-4 h-4 text-cyan-400" />
            <h3 className="font-semibold text-white hover:text-cyan-300 transition-colors">{p.profile.business_name || p.name}</h3>
            <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${
              p.profile.status === "approved" ? "text-emerald-400 border-emerald-400/30" :
              p.profile.status === "rejected" ? "text-[#FF5E7E] border-[#FF5E7E]/30" : "text-amber-400 border-amber-400/30"
            }`}>{p.profile.status}</span>
            <span className="text-xs text-slate-500 ml-auto">{p.email} · {p.profile.phone}</span>
            <span className="text-[10px] text-cyan-400/70 uppercase tracking-widest">View profile →</span>
          </div>
          <div className="flex flex-wrap gap-2 mb-3">
            {[["Business License", p.profile.business_license_url], ["Fleet Insurance", p.profile.fleet_insurance_url], ["Rental Agreement", p.profile.rental_agreement_url]].map(([label, url]) => (
              <a key={label} href={url ? fileUrl(url) : "#"} target="_blank" rel="noreferrer" onClick={stop}
                className="text-xs flex items-center gap-1.5 px-3 py-1.5 rounded-lg glass text-cyan-300 hover:text-white">
                <FileText className="w-3.5 h-3.5" /> {label} <ExternalLink className="w-3 h-3" />
              </a>
            ))}
          </div>
          {p.profile.status === "pending" && (
            <div className="flex gap-2">
              <button data-testid={`approve-provider-${p.id}`} onClick={(e) => { stop(e); act(p.id, "approve"); }} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"><Check className="w-4 h-4" /> Approve</button>
              <button data-testid={`reject-provider-${p.id}`} onClick={(e) => { stop(e); act(p.id, "reject"); }} className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#FF5E7E]/15 text-[#FF5E7E] hover:bg-[#FF5E7E]/25"><X className="w-4 h-4" /> Reject</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function VehiclesQueue({ onChange }) {
  const [cars, setCars] = useState(null);
  const [filter, setFilter] = useState("pending");
  const load = (f) => api.get("/admin/cars", { params: f ? { status: f } : {} }).then(({ data }) => setCars(data));
  useEffect(() => { load(filter); }, [filter]);

  const act = async (id, action) => {
    try {
      await api.post(`/admin/cars/${id}/${action}`);
      toast.success(`Vehicle ${action}d`);
      load(filter); onChange && onChange();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-vehicles">
      <div className="flex gap-2 mb-4">
        {["pending", "approved", "rejected", ""].map((f) => (
          <button key={f || "all"} data-testid={`vehicle-filter-${f || "all"}`} onClick={() => setFilter(f)}
            className={`text-xs px-3 py-1.5 rounded-full capitalize ${filter === f ? "btn-neon font-semibold" : "glass text-slate-300"}`}>
            {f || "all"}
          </button>
        ))}
      </div>
      {cars === null ? <Loading /> : cars.length === 0 ? <Empty text="No vehicles in this queue." /> : (
        <div className="space-y-3">
          {cars.map((c) => (
            <div key={c.id} className="glass rounded-xl p-4 flex flex-col sm:flex-row gap-4 items-start" data-testid={`admin-vehicle-${c.id}`}>
              <img src={c.image_url ? fileUrl(c.image_url) : "https://images.unsplash.com/photo-1503376780353-7e6692767b70?q=85"} alt="" className="w-full sm:w-28 h-20 rounded-lg object-cover" />
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-white">{c.name}</h3>
                  <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${
                    c.status === "approved" ? "text-emerald-400 border-emerald-400/30" :
                    c.status === "rejected" ? "text-[#FF5E7E] border-[#FF5E7E]/30" : "text-amber-400 border-amber-400/30"}`}>{c.status}</span>
                </div>
                <p className="text-xs text-slate-400 mt-1">{c.provider_name} · {c.car_type} · {c.island}</p>
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-400 flex-wrap">
                  <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {c.passenger_capacity}</span>
                  <span className="flex items-center gap-1"><Fuel className="w-3.5 h-3.5" /> {c.fuel_policy}</span>
                  {c.delivery_available && <span className="flex items-center gap-1 text-emerald-400"><Truck className="w-3.5 h-3.5" /> ${c.delivery_fee}</span>}
                  <span className="text-white font-semibold">${c.price_per_day}/day</span>
                </div>
              </div>
              {c.status === "pending" && (
                <div className="flex gap-2">
                  <button data-testid={`approve-vehicle-${c.id}`} onClick={() => act(c.id, "approve")} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25"><Check className="w-4 h-4" /> Approve</button>
                  <button data-testid={`reject-vehicle-${c.id}`} onClick={() => act(c.id, "reject")} className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-semibold bg-[#FF5E7E]/15 text-[#FF5E7E] hover:bg-[#FF5E7E]/25"><X className="w-4 h-4" /> Reject</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function AgentManagement() {
  const [status, setStatus] = useState("pending");
  const [agents, setAgents] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [baseRate, setBaseRate] = useState("");
  const [savingRate, setSavingRate] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/admin/agents", { params: { status: status || undefined } }),
      api.get("/admin/agent-payouts"),
      api.get("/admin/settings"),
    ]).then(([a, p, s]) => {
      setAgents(a.data); setPayouts(p.data);
      setBaseRate(String(Math.round((s.data.agent_base_rate || 0) * 100)));
    }).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, [status]); // eslint-disable-line

  const review = async (id, action) => {
    try {
      await api.post(`/admin/agents/${id}/${action}`);
      toast.success(`Agent ${action === "approve" ? "approved" : "rejected"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const saveRate = async () => {
    setSavingRate(true);
    try {
      await api.post("/admin/settings/agent-base-rate", { agent_base_rate: Math.max(0, Number(baseRate) || 0) / 100 });
      toast.success("Global base commission updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSavingRate(false); }
  };

  const markPaid = async (pid) => {
    try { await api.post(`/admin/agent-payouts/${pid}/paid`); toast.success("Payout marked paid"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const pendingPayouts = payouts.filter((p) => p.status === "requested");

  return (
    <div data-testid="admin-agent-management">
      {/* Global base commission */}
      <div className="glass rounded-2xl p-5 mb-6 flex flex-wrap items-end gap-4">
        <div>
          <p className="font-heading font-semibold text-white flex items-center gap-2"><BadgePercent className="w-4 h-4 text-cyan-400" /> Global base agent commission</p>
          <p className="text-slate-400 text-sm mt-1">The default % every approved agent earns on affiliate bookings (before any provider bonus).</p>
        </div>
        <div className="flex items-end gap-2">
          <div className="relative">
            <input type="number" min={0} max={50} step={0.5} data-testid="agent-base-rate-input" value={baseRate}
              onChange={(e) => setBaseRate(e.target.value)}
              className="w-28 bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-3 py-2.5 text-sm text-white outline-none focus:border-cyan-400/50 pr-7" />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm">%</span>
          </div>
          <button onClick={saveRate} disabled={savingRate} data-testid="agent-base-rate-save" className="btn-neon rounded-xl px-4 py-2.5 text-sm font-semibold flex items-center gap-2 disabled:opacity-60">
            {savingRate ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Save
          </button>
        </div>
      </div>

      {/* Pending payouts */}
      {pendingPayouts.length > 0 && (
        <div className="glass rounded-2xl p-5 mb-6" data-testid="admin-agent-payouts">
          <p className="font-heading font-semibold text-white mb-3 flex items-center gap-2"><Wallet className="w-4 h-4 text-emerald-400" /> Payout requests</p>
          <div className="divide-y divide-[#1D2640]/60">
            {pendingPayouts.map((p) => (
              <div key={p.id} className="flex items-center justify-between py-2.5 text-sm" data-testid={`agent-payout-${p.id}`}>
                <span className="text-slate-300">{p.agent_name} <span className="text-slate-500">@{p.agent_handle}</span></span>
                <span className="text-emerald-400 font-semibold">${(p.amount ?? 0).toLocaleString()}</span>
                <button onClick={() => markPaid(p.id)} data-testid={`agent-payout-paid-${p.id}`} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25">Mark paid</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Agent list */}
      <div className="flex gap-2 mb-4">
        {["pending", "approved", "rejected"].map((s) => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`agent-filter-${s}`}
            className={`text-xs font-semibold px-3 py-1.5 rounded-full border capitalize ${status === s ? "btn-neon border-transparent" : "glass border-[#1D2640] text-slate-300"}`}>
            {s}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="w-7 h-7 animate-spin text-cyan-400" /></div>
      ) : agents.length === 0 ? (
        <div className="text-center py-12 glass rounded-2xl"><p className="text-slate-400 text-sm">No {status} agents.</p></div>
      ) : (
        <div className="glass rounded-2xl overflow-hidden overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]">
            <thead>
              <tr className="text-left text-xs text-slate-400 border-b border-[#1D2640]">
                <th className="px-5 py-3 font-medium">Agent</th>
                <th className="px-5 py-3 font-medium">Handle</th>
                <th className="px-5 py-3 font-medium">Email</th>
                <th className="px-5 py-3 font-medium text-right">Earned</th>
                <th className="px-5 py-3 font-medium text-right">Balance</th>
                <th className="px-5 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {agents.map((a) => (
                <tr key={a.id} data-testid={`admin-agent-${a.id}`} className="border-b border-[#1D2640]/50">
                  <td className="px-5 py-3 text-white">{a.name}
                    {a.business_name && <span className="block text-[11px] text-slate-400">{a.business_name}{a.phone ? ` · ${a.phone}` : ""}</span>}
                    {!a.email_verified && <span className="ml-2 text-[10px] text-amber-400 border border-amber-400/30 rounded px-1.5 py-0.5">Email pending</span>}
                  </td>
                  <td className="px-5 py-3 text-cyan-300">@{a.handle}</td>
                  <td className="px-5 py-3 text-slate-400">{a.email}</td>
                  <td className="px-5 py-3 text-right text-slate-300">${(a.total_earned ?? 0).toLocaleString()}</td>
                  <td className="px-5 py-3 text-right text-emerald-400">${(a.balance ?? 0).toLocaleString()}</td>
                  <td className="px-5 py-3 text-right">
                    {a.status === "pending" ? (
                      <div className="flex items-center justify-end gap-2">
                        <button onClick={() => review(a.id, "approve")} data-testid={`approve-agent-${a.id}`} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25">Approve</button>
                        <button onClick={() => review(a.id, "reject")} data-testid={`reject-agent-${a.id}`} className="text-xs font-semibold px-3 py-1.5 rounded-lg bg-[#FF5E7E]/15 text-[#FF5E7E] hover:bg-[#FF5E7E]/25">Reject</button>
                      </div>
                    ) : (
                      <span className={`text-xs px-2 py-0.5 rounded-full border capitalize ${a.status === "approved" ? "text-emerald-400 border-emerald-400/30" : "text-[#FF5E7E] border-[#FF5E7E]/30"}`}>{a.status}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
